import PropTypes from 'prop-types';
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import CloseIcon from '@mui/icons-material/Close';
import { isNil } from 'lodash';

import SwitchField from 'common/forms/SwitchField';

const NEW_PRODUCTS_ACTIVE_BY_DEFAULT_LABEL = 'Make new products available automatically';

const PartnerCatalogConfigModal = ({
  showModal,
  loading,
  handleClose,
  partner,
  handleCatalogConfig,
}) => {
  const initialValue = isNil(partner.newProductsActiveByDefault)
    ? true
    : partner.newProductsActiveByDefault;
  const [newProductsActiveByDefault, setNewProductsActiveByDefault] = useState(initialValue);

  const catalogConfigChanged = newProductsActiveByDefault !== initialValue;

  const handleToggle = () => setNewProductsActiveByDefault((prev) => !prev);

  const handleConfirm = () => {
    handleCatalogConfig({ partnerUuid: partner.partnerUuid, newProductsActiveByDefault });
  };

  return (
    <Dialog
      open={showModal}
      onClose={handleClose}
      aria-describedby='Partner-catalog-config'
      fullWidth
      PaperProps={{ sx: { maxWidth: 520 } }}
    >
      <DialogTitle sx={{ p: 2, backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          Catalog Config
          <IconButton aria-label='close' onClick={handleClose} sx={{ color: 'inherit' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={1} sx={{ mt: 3 }}>
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Typography variant='subtitle1'>{NEW_PRODUCTS_ACTIVE_BY_DEFAULT_LABEL}</Typography>
            <SwitchField
              label={NEW_PRODUCTS_ACTIVE_BY_DEFAULT_LABEL}
              checked={newProductsActiveByDefault}
              onClick={handleToggle}
            />
          </Stack>
          <Typography variant='body2' color='text.secondary'>
            When a new product is added for this partner, it will be available for ordering right
            away. If it&apos;s turned off, the partner has to make the catalog active for future
            orders.
          </Typography>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button variant='outlined' color='secondary' onClick={handleClose}>
          Cancel
        </Button>
        <LoadingButton
          variant='contained'
          loading={loading}
          disabled={loading || !catalogConfigChanged}
          onClick={handleConfirm}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default PartnerCatalogConfigModal;

PartnerCatalogConfigModal.propTypes = {
  partner: PropTypes.shape({
    partnerUuid: PropTypes.string.isRequired,
    partnerName: PropTypes.string.isRequired,
    newProductsActiveByDefault: PropTypes.bool,
  }).isRequired,
  showModal: PropTypes.bool,
  loading: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  handleCatalogConfig: PropTypes.func.isRequired,
};

PartnerCatalogConfigModal.defaultProps = {
  showModal: false,
  loading: false,
};
